/**
 * Transient Present-Mode signals (S13). Two things travel through here because
 * the components that produce and consume them have no direct handle on each
 * other:
 *
 * 1. `requestedDashboardId` — the Object Explorer "Present" context-menu item
 *    opens/focuses the single dashboard workspace tab (`openDashboardTab`) and
 *    then needs to tell THAT tab instance to enter Present Mode for the
 *    dashboard just picked. DashboardTab consumes and clears it once the
 *    matching dashboard resolves (it deliberately doesn't persist the request).
 * 2. `cycle` — the live playlist auto-advance position, published by
 *    `usePresentPlaylist` (which owns the tick) and read by `PresentMode`'s
 *    countdown ring. `DashboardTab` sits between the two and owns neither, so
 *    a prop can't carry it. Nothing here schedules anything: the value is a
 *    mirror of the hook's existing 1s tick, never a second timer.
 *
 * Neither field is persisted.
 */
import { create } from 'zustand';

export type PresentCycle = {
  /** ms the current dashboard has been shown. */
  elapsedMs: number;
  /** Full display window in ms; 0 when the playlist isn't auto-advancing. */
  cycleMs: number;
};

const IDLE_CYCLE: PresentCycle = { elapsedMs: 0, cycleMs: 0 };

type PresentRequestState = {
  requestedDashboardId: string | null;
  request: (dashboardId: string) => void;
  consume: () => void;
  cycle: PresentCycle;
  setCycle: (cycle: PresentCycle) => void;
};

export const usePresentRequestStore = create<PresentRequestState>((set) => ({
  requestedDashboardId: null,
  request: (dashboardId) => set({ requestedDashboardId: dashboardId }),
  consume: () => set({ requestedDashboardId: null }),

  cycle: IDLE_CYCLE,
  // Identity-stable on no-op writes: the publisher fires once per tick, and an
  // unchanged value must not wake the ring's subscriber.
  setCycle: (next) =>
    set((s) =>
      s.cycle.elapsedMs === next.elapsedMs && s.cycle.cycleMs === next.cycleMs
        ? s
        : { cycle: next },
    ),
}));
